



































import { Component, Vue, Prop } from "vue-property-decorator";
import notificationService from "@/services/notificationService";
import Card from "@/components/default/Card.vue";
import Datagrid from "@/components/default/Datagrid.vue";
import MeasurementMergePopup from "@/modules/measurements/components/MeasurementsMergePopup.vue";
import { selectableEmployees } from "@/modules/employees/employeeSelection";
import {
  MeasurementsClient,
  CreateMeasurementCommand,
  DuplicateMeasurementCommand,
  IUpdateMeasurementBasicsCommand,
  UpdateMeasurementBasicsCommand,
  IMeasurementListItemDto,
  MeasuringStatesClient,
  GenerateMeasuringStateFromMeasurementCommand,
  Measurement,
} from "@/data/web-api-client";

@Component({
  components: {
    Card,
    Datagrid,
    MeasurementMergePopup,
  },
})
export default class MeasurementsTable extends Vue {
  @Prop({ required: true })
  projectId!: string;

  @Prop({ required: true })
  projectType!: string;

  @Prop({ required: false, default: false})
  disabled!: boolean;

  data: IMeasurementListItemDto[] = [];
  usedReferences: string[] = [];
  loading: boolean = true;
  nextMeasurementNumber = '';

  created() {
    this.loadData();
    this.loadUsedReferences();
  }

  async loadData() {
    try {
      this.loading = true;
      this.nextMeasurementNumber = await new MeasurementsClient(undefined, this.$axios).getNextMeasurementNumber(this.projectId);
      this.data = await new MeasurementsClient(undefined, this.$axios).getList(this.projectId);
    } catch {
      notificationService.showErrorMessage("Er is iets misgelopen tijdens het laden van de opmetingen");
    } finally {
      this.loading = false;
    }
  }

  async loadUsedReferences() {
    try {
      this.loading = true;
      this.usedReferences = await new MeasurementsClient(undefined, this.$axios).getUsedReferences(this.projectId);
    } catch {
      notificationService.showErrorMessage("Er is iets misgelopen tijdens het laden van de gebruikte referenties");
    } finally {
      this.loading = false;
    }
  }

  openDetails(e: any) {
    if (e.rowType == 'group') {
      return;
    }

    if (e && e.row && !e.row.isEditing && e.column && e.column.type !== "buttons" && e.column.type !== "selection") {
      this.$router.push({
        path: `/projects/${this.projectId}/measurements/${e.row.data.documentNumber.replace("/", "-").replace("/", "-")}`,
      });
    }
  }

  addRow() {
    this.GridInstance.addRow();
  }

  async generateQuotation(e: any) {
    const grid = this.$refs.grid as Datagrid;
    if (grid) {
      const selectedRows: IMeasurement[] = (grid.$refs.measurementsTable as any).instance.getSelectedRowsData();
      const nameList = selectedRows.map((o) => o.documentNumber);
      try {
        this.$emit("generateQuotation", {
          projectId: this.projectId,
          measurementNames: nameList,
        });
      } catch (err) {
        notificationService.showAxiosError(err);
      }
    }
  }

  async generateMeasuringState(e: any) {
    const grid = this.$refs.grid as Datagrid;

    if (grid) {
      const selectedRows: Measurement[] = (grid.$refs.measurementsTable as any).instance.getSelectedRowsData();

      if (selectedRows.some((sr) => sr.measurementType?.toLowerCase() != "gecorrigeerd")) {
        const faultyRows = selectedRows.filter((sr) => sr.measurementType?.toLowerCase() != "gecorrigeerd").map((sr) => ` ${sr.documentNumber!}: ${sr.name}`);

        notificationService.showErrorMessage(`Enkel van gecorrigeerde opmetingen kan er een meetstaat gegenereerd worden. Corrigeer volgende opmetingen: ${faultyRows.join(",")}`);
        return;
      }

      const nameList = selectedRows.map((o) => o.documentNumber!);

      if (nameList === undefined) throw Error("nameList is empty");

      try {
        const result = await new MeasuringStatesClient(undefined, this.$axios).generateFromMeasurement(
          new GenerateMeasuringStateFromMeasurementCommand({
            projectId: this.projectId,
            measurementNames: nameList,
          })
        );

        this.$router.push({
          path: `/measuringstates/${result.replaceAll("/", "-")}`,
        });
      } catch (err) {
        notificationService.showAxiosError(err);
      }
    }
  }

  async merge() {
    const grid = this.$refs.grid as Datagrid;
    if (grid) {
      try {
        const selectedRows: IMeasurement[] = (grid.$refs.measurementsTable as any).instance.getSelectedRowsData();
        const nameList = selectedRows.map((o) => o.documentNumber);
        if (nameList.length < 1) {
          alert("Er zijn geen opmetingen geselecteerd!");
          return;
        }
        if (nameList.length < 2) {
          alert("Gelieve meerdere opmetingen te selecteren!");
          return;
        }
        const popup = this.$refs.MeasurementMergePopup as MeasurementMergePopup;
        popup.model.list = nameList;
        popup.model.projectId = this.projectId;
        popup.show = true;
      } catch (err) {
        notificationService.showAxiosError(err);
      }
    }
  }

  async duplicateMeasurement(e: any) {
    if (this.projectId) {
      const newName = prompt(this.$t('messages.EnterValue') as string, e.row.data.name + ' copy');
      if (newName != null && newName !== '') {
        const documentNumber = await new MeasurementsClient(undefined, this.$axios).duplicateMeasurement(
          this.projectId,
          new DuplicateMeasurementCommand({
            projectId: this.projectId,
            documentNumber: e.row.data.documentNumber,
            name: newName,
          })
        );

        this.loadData();
      }
    }
  }

  createMeasurement(e: any) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        const documentNumber = await new MeasurementsClient(undefined, this.$axios).createMeasurement(
          this.projectId,
          new CreateMeasurementCommand({
            projectId: this.projectId,
            name: e.data.name,
            recorder: e.data.recorder,
            date: e.data.date,
            projectType: e.data.projectType,
            workType: e.data.workType,
            measurementType: e.data.measurementType,
            reference: e.data.reference,
          })
        );

        self.GridInstance.cancelEditData();
        this.loadData();
        this.loadUsedReferences();
        notificationService.saved();

        resolve(true);
      } catch (err) {
        notificationService.failedToSave();
        reject(true);
      }
    });
  }

  updateMeasurement(e: any) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        const updatedData: IUpdateMeasurementBasicsCommand = Object.assign({}, e.oldData, e.newData);
        await new MeasurementsClient(undefined, this.$axios).updateMeasurementBasics(this.projectId, new UpdateMeasurementBasicsCommand(updatedData));

        // Manual add
        self.GridInstance.cancelEditData();
        this.loadData();
        this.loadUsedReferences();
        notificationService.saved();

        resolve(true);
      } catch (err) {
        notificationService.failedToSave();
        reject(true);
      }
    });
  }

  deleteRow(e: any) {
    e.cancel = new Promise(async (resolve) => {
      try {
        await new MeasurementsClient(undefined, this.$axios).deleteMeasurement(this.projectId, e.data.documentNumber.replace('/', '-').replace('/', '-'));

        notificationService.saved();
        this.loadData();
        this.loadUsedReferences();

        resolve(true);
      } catch (err) {
        notificationService.failedToSave();
        resolve(true);
      }
    });
  }

  initMeasurement(e: any) {
    e.data.documentNumber = this.nextMeasurementNumber;
    e.data.name = "Opmeting";

    const currentUser = this.$store.getters.currentUser;
    let employee = null;

    if (currentUser) {
      employee = this.$store.getters['employees/itemByUserId'](currentUser.localAccountId);
    }

    e.data.recorder = employee ? employee.id : '';
    e.data.date = new Date();
    e.data.status = 0;
    e.data.workType = this.worktypes[0];
    e.data.measurementType = this.measurementTypes[0];
    e.data.projectType = this.projectType;
  }

  get GridInstance() {
    return ((this.$refs.grid as Vue).$refs.measurementsTable as any).instance;
  }

  get columns() {
    const self = this;

    const columns: any[] = [
      {
        dataField: 'documentNumber',
        caption: 'Nummer',
      },
      {
        dataField: "name",
        caption: "Naam",
      },
      {
        dataField: "recorder",
        caption: "Door",
        lookup: {
          // OS-214: hide disabled employees from new picks, but keep whoever already recorded
          // an existing measurement selectable/visible on that row.
          dataSource: selectableEmployees(this.$store.getters["employees/items"], {
            currentValue: this.data.map((item: any) => item.recorder).filter(Boolean),
          }),
          displayExpr: "displayName",
          valueExpr: "id",
        },
      },
      {
        dataField: 'header',
        caption: 'Referentie',
        groupIndex: 0,
      },
      {
        dataField: "reference",
        caption: "Referentie",
        lookup: {
          dataSource: this.usedReferences,
        },
        editorOptions: {
          acceptCustomValue: true,
          onCustomItemCreating: (args: any) => {
            if (args.element.parentElement.nodeName != 'DIV') {
              if (!this.usedReferences.includes(args.text)) {
                this.usedReferences.splice(this.usedReferences.length - 1, 0, args.text);
              }
              args.customItem = args.text;
            }
          },
        },
      },
      {
        dataField: "date",
        caption: "Datum",
        dataType: "date",
        format: "dd/MM/yyyy",
      },
      {
        dataField: "projectType",
        caption: "Type project",
        lookup: {
          dataSource: this.projecttypes,
        },
      },
      {
        dataField: "workType",
        caption: "Soort Werk",
        lookup: {
          dataSource: this.worktypes,
        },
      },
      {
        dataField: "measurementType",
        caption: "Type",
        lookup: {
          dataSource: this.measurementTypes,
        },
      },
      {
        dataField: "status",
        caption: "Status",
        lookup: {
          dataSource: this.Statusses,
          displayExpr: "text",
          valueExpr: "value",
        },
      },
    ];

    if (!this.disabled) {
      columns.push({
        type: "buttons",
        buttons: [
          {
            hint: "Duplicate",
            icon: "fas fa-copy",
            visible(e: any) {
              return !e.row.isEditing && !e.row.data.isCompleted && !self.disabled;
            },
            onClick: this.duplicateMeasurement,
          },
          'edit',
          'delete',
        ],
      });
    }

    return columns;
  }

  get projecttypes() {
    const catalog = this.$store.getters["catalogs/list"]("ProjectTypes");
    return catalog ? catalog.values : [];
  }

  get worktypes() {
    const catalog = this.$store.getters["catalogs/list"]("WorkTypes");
    return catalog ? catalog.values : [];
  }

  get measurementTypes() {
    const catalog = this.$store.getters["catalogs/list"]("MeasurementTypes");
    return catalog ? catalog.values : [];
  }

  get Statusses() {
    return [
      { value: 0, text: this.$t("objects.measurements.Statusses.0") },
      { value: 1, text: this.$t("objects.measurements.Statusses.1") },
      { value: 2, text: this.$t("objects.measurements.Statusses.2") },
      { value: 3, text: this.$t("objects.measurements.Statusses.3") },
    ];
  }

  get groupingConfig() {
    return {
      allowCollapsing: false,
      autoExpandAll: true,
      contextMenuEnabled: false,
    };
  }
}
